import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Button, ActionButton } from 'src/app/components';
import { InputSelect, InputTextField } from 'src/app/components/HookForm';
import { SalesPromoTransactionProp } from './SalesPromoTransaction.page';
import { defaultSalesPromoPrizes } from '../SalesPromoForm3.hook';
import { PrizeListModel, SalesPromoRuleModel, TransactionRule } from 'src/app/modules/campaign/models/SalesPromo.model';
import { useGetPrizeMasterListSalesPromo } from 'src/app/modules/campaign/providers/SalesPromo.provider';
import { useEffect } from 'react';


interface SalesPromoPrizeProps extends SalesPromoTransactionProp {
    ruleIndex: number
}

const SalesPromoPrize: React.FC<SalesPromoPrizeProps> = ({ id, storeIndex, ruleIndex, prizeRuleLength, is_mission_collect_stamp }) => {
    const form = useFormContext(); // Get the full form object
    const controlName = `prize_rules.${storeIndex}.sales_promo_rules.${ruleIndex}.prizes`
    const { control, setValue, getValues, clearErrors, reset } = form;
    const { fields, append } = useFieldArray({
        control: control,
        name: `prize_rules.${storeIndex}.sales_promo_rules.${ruleIndex}.prizes`
    });
    const errors = form.formState.errors as any;
    const prizeListApi = useGetPrizeMasterListSalesPromo(id ?? '', { page: 1, limit: 1000 })
    const prizeListContent: any[] = prizeListApi.data?.content ?? []
    const transformData = () => {
        const hasProduct = prizeListContent.some(item => item.type === "PRODUCT");
        const prizes = prizeListContent.filter(item => item.type !== "PRODUCT");

        const result = [...prizes];

        if (is_mission_collect_stamp === true) {
            result.push({ id: "STAMP", name: "STAMP" });
        }

        if (hasProduct) {
            result.push({ id: "PRODUCT", name: "Direct Gift" });
        }

        return result;
    };

    useEffect(() => {
        if (is_mission_collect_stamp) {
            handleInputChange({ id: 'STAMP', name: 'STAMP' }, 0, 'prizes');
            handlePrizeChange({ id: 'STAMP', name: 'STAMP' }, 0);
        }
    }, [fields, is_mission_collect_stamp])

    const selectedPrizes = useWatch({ control, name: `prize_rules.${storeIndex}.sales_promo_rules.${ruleIndex}.prizes` });

    // Exclude already selected prize types, except for the current row
    const getFilteredOptions = () => {
        const extractedPrizes = selectedPrizes.flatMap((item: any) => item.prizes);
        return transformData().filter(item =>
            item?.id && !extractedPrizes.some((extractedPrizes: any) => extractedPrizes?.id === item?.id)
        );
    };

    const getDisabled = (index: number): boolean => {
        const prizeRules = prizeRuleLength?.prize_rules;

        if (!prizeRules || storeIndex === undefined || !prizeRules[storeIndex]?.sales_promo_rules) {
            return false;
        }

        const transactionRules = prizeRules[storeIndex].sales_promo_rules;

        if (!transactionRules || ruleIndex === undefined || !transactionRules[ruleIndex]) {
            return false;
        }

        return (transactionRules[ruleIndex].prizes_length ?? 0) > index;
    };

    const handleInputChange = (value: any, index: number, key: string) => {
        setValue(`${controlName}.${index}.${key}`, value);

        // Clear the error if the value is not empty
        if (value) {
            clearErrors(`${controlName}.${index}.${key}`);
        }
    };

    const deletePrize = (delIndex: number) => {
        const currentData = getValues()
        const newValuePrizes = selectedPrizes.filter((_: any, index: number) => index !== delIndex)
        const updatedSalesPromoData = {
            prize_rules: currentData.prize_rules.map((store: SalesPromoRuleModel, sIndex: number) =>
                sIndex === storeIndex
                    ? {
                        ...store,
                        sales_promo_rules: store.sales_promo_rules!.map(
                            (rule: TransactionRule, rIndex: number) =>
                                rIndex === ruleIndex ? { ...rule, prizes: newValuePrizes } : rule
                        ),
                    }
                    : store
            ),
        };
        reset(updatedSalesPromoData)
    }

    const handlePrizeChange = (data: PrizeListModel, index: number) => {
        let type = data.type;
        let prize_ids = [data.id];

        if (data.id === "PRODUCT" || data.id === "STAMP") {
            type = data.id;
            prize_ids = [];
        }

        setValue(`${controlName}.${index}.type`, type);
        setValue(`${controlName}.${index}.prize_ids`, prize_ids);
    }

    return (
        <div className="border border-gray-300 rounded-lg col-span-2">
            <table className="min-w-full">
                <thead>
                    <tr className="text-left text-gray-700">
                        <th className="p-2">Gift/Voucher</th>
                        <th className="p-2">Get Gift/Voucher</th>
                        <th className="p-2">Max Gift/Voucher</th>
                        <th className="p-2"></th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map((_, index) => (
                        <tr key={'prize' + index}>
                            <td className="p-2">
                                <InputSelect
                                    onChangeValue={(data) => handlePrizeChange(data as PrizeListModel, index)}
                                    placeholder="Select Gift/Voucher"
                                    isMulti={false}
                                    options={getFilteredOptions()}
                                    rule={{
                                        required: !getDisabled(index) ? 'Field cannot be empty' : false,
                                    }}
                                    form={form}
                                    isDisabled={getDisabled(index) || is_mission_collect_stamp}
                                    name={`prize_rules.${storeIndex}.sales_promo_rules.${ruleIndex}.prizes.${index}.prizes`}
                                    selectOption={(_) => ({
                                        label: _.name!,
                                        value: _.id!,
                                    })}
                                />
                            </td>

                            <td className="p-2">
                                <InputTextField
                                    placeholder="Input Gift/Voucher"
                                    name={`prize_rules.${storeIndex}.sales_promo_rules.${ruleIndex}.prizes.${index}.quantity`}
                                    type="number"
                                    min={0}
                                    onChange={(e) => handleInputChange(e.target.value, index, 'quantity')}
                                    rule={{
                                        required: 'Field cannot be empty',
                                        min: {
                                            value: 1,
                                            message: 'Minimal qty is 1',
                                        },
                                    }}
                                    error={errors?.prize_rules?.[storeIndex]?.sales_promo_rules?.[ruleIndex]?.prizes?.[index]?.quantity?.message}
                                    helperText={errors?.prize_rules?.[storeIndex]?.sales_promo_rules?.[ruleIndex]?.prizes?.[index]?.quantity?.message}
                                />
                            </td>
                            <td className="p-2">
                                <InputTextField
                                    type="number"
                                    placeholder="Input Max Gift/Voucher"
                                    min={0}
                                    onChange={(e) => handleInputChange(e.target.value, index, 'max_quantity_per_transaction')}
                                    name={`prize_rules.${storeIndex}.sales_promo_rules.${ruleIndex}.prizes.${index}.max_quantity_per_transaction`}
                                    rule={{
                                        required: 'Field cannot be empty',
                                        min: {
                                            value: getValues(`prize_rules.${storeIndex}.sales_promo_rules.${ruleIndex}.prizes.${index}.quantity`) || 1,
                                            message: 'Max Coupon cannot be less than Get Coupon. Please enter a valid amount.',
                                        },
                                    }}
                                    error={errors?.prize_rules?.[storeIndex]?.sales_promo_rules?.[ruleIndex]?.prizes?.[index]?.max_quantity_per_transaction?.message}
                                    helperText={errors?.prize_rules?.[storeIndex]?.sales_promo_rules?.[ruleIndex]?.prizes?.[index]?.max_quantity_per_transaction?.message}
                                />
                            </td>
                            <td className="p-2">
                                <ActionButton disabled={getDisabled(index) || fields.length === 1} variant="delete" onClick={() => deletePrize(index)} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {!is_mission_collect_stamp && <div className="flex items-center justify-between p-4 mt-2 bg-gray-100 rounded-b-lg">
                <span>Want to add more prizes?</span>
                <Button
                    variant="outlined"
                    onClick={() => append(defaultSalesPromoPrizes)}
                >
                    Add Prize +
                </Button>
            </div>}
        </div>
    );
};

export default SalesPromoPrize;
